import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Pill } from '@/components/Pill';
import { Section, SectionHeader } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { fetchSurveys } from '@/queries/surveys/system';
import { SurveyCard, SurveyCardSkeleton } from './-fragments/SurveyCard';

export const Route = createFileRoute('/system/_auth/organizations/$organizationId/surveys/')({
    loader: async ({ params }) => {
        const { organizationId } = params;
        return fetchSurveys(organizationId);
    },
    pendingComponent: SystemShowOrganizationSurveysRouteSkeleton,
    errorComponent: SystemShowOrganizationSurveysRouteError,
    component: SystemShowOrganizationSurveysRoute,
});

function SystemShowOrganizationSurveysRouteError({ reset }: ErrorComponentProps) {
    return <ErrorOverlay active onRetry={reset} />;
}

function SystemShowOrganizationSurveysRoute() {
    const { organizationId } = Route.useParams();
    const { surveys } = Route.useLoaderData();
    const navigate = Route.useNavigate();

    return (
        <Section>
            <SectionHeader title="サーベイ一覧" extras={<Pill>{surveys.length}件</Pill>} />

            {surveys.length > 0 ? (
                <Flex as="ul" direction="column" gap="4" align="start">
                    {surveys.map((survey) => (
                        <li key={survey.surveyId}>
                            <SurveyCard
                                survey={survey}
                                onOpen={() =>
                                    navigate({
                                        to: `/system/surveys`,
                                        search: { organizationId, surveyId: survey.surveyId },
                                    })
                                }
                            />
                        </li>
                    ))}
                </Flex>
            ) : (
                // TODO: Visual design for empty state
                <div>サーベイがありません。</div>
            )}
        </Section>
    );
}

function SystemShowOrganizationSurveysRouteSkeleton() {
    return (
        <Section>
            <SectionHeader title="サーベイ一覧" extras={<Skeleton.Line width={50} height="xl" />} />

            <Flex as="ul" direction="column" gap="4" align="start">
                {Array.from({ length: 3 }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, cards have no identity
                    <li key={i}>
                        <SurveyCardSkeleton />
                    </li>
                ))}
            </Flex>
        </Section>
    );
}
